import { AsyncPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { type PopulationItem } from '@bizzdesign/data-access-populations';
import { UserAvatarDirective } from '@bizzdesign/shared/directives';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { type ActionButton, type BDMenuItem } from '@bizzdesign/shared/models';
import {
  ToAvatarsPipe,
  TranslateMenuItemsPipe,
} from '@bizzdesign/shared/pipes';
import { CallToActionComponent } from '@bizzdesign/ui-call-to-action';
import { SearchbarComponent } from '@bizzdesign/ui-searchbar';
import { PageHeaderComponent } from '@bizzdesign/ui-page-header';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { SkeletonModule } from 'primeng/skeleton';
import {
  type Table,
  type TableLazyLoadEvent,
  TableModule,
} from 'primeng/table';
import { ReplaySubject } from 'rxjs';

@Component({
  selector: 'bd-populations-overview',
  templateUrl: './populations-overview.component.html',
  styleUrl: './populations-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SharedI18nModule,
    TableModule,
    SearchbarComponent,
    PageHeaderComponent,
    AvatarModule,
    UserAvatarDirective,
    DatePipe,
    AsyncPipe,
    ButtonModule,
    MenuModule,
    TranslateMenuItemsPipe,
    SkeletonModule,
    CallToActionComponent,
    ToAvatarsPipe,
  ],
})
export class PopulationsOverviewComponent {
  readonly SCROLLABLE_ITEMS = 10;
  readonly SCROLL_HEIGHT = 56;

  @Input() populations: PopulationItem[] = [];
  @Input() isLoading = false;

  @Output() loadMorePopulations: EventEmitter<void> = new EventEmitter<void>();
  @Output() searchPopulations: EventEmitter<string> =
    new EventEmitter<string>();
  @Output() newPopulationCreated = new EventEmitter<void>();
  @Output() addPopulationToFrame = new EventEmitter<PopulationItem>();
  @Output() editDataForObjectsInPopulation = new EventEmitter<PopulationItem>();
  @Output() openDeletePopulation = new EventEmitter<PopulationItem>();
  @ViewChild('populationsOverviewTable') populationsTable: Table | undefined;

  searchSuggestions$ = new ReplaySubject<string[]>(1);
  selectedPopulation: PopulationItem | undefined;
  actionMenuItems: BDMenuItem[] = [
    {
      labelKey: 'populationOverview.addTo',
      tScope: 'featurePopulations',
      command: () => this.addPopulationToFrame.emit(this.selectedPopulation),
    },
    {
      labelKey: 'populationOverview.editData',
      tScope: 'featurePopulations',
      command: () =>
        this.editDataForObjectsInPopulation.emit(this.selectedPopulation),
    },
    { separator: true },
    {
      labelKey: 'populationOverview.delete',
      tScope: 'featurePopulations',
      styleClass: 'bd-destructive',
      icon: 'ui-delete',
      command: () => this.openDeletePopulation.emit(this.selectedPopulation),
    },
  ];

  newPopulationAction: ActionButton = {
    labelKey: 'populationOverview.newPopulation',
    tScope: 'featurePopulations',
    onClick: () => this.onCreateNewPopulationClick(),
    icon: 'pi pi-arrow-right',
    iconPos: 'right',
  };
  newPopulationActionHeader: ActionButton = {
    ...this.newPopulationAction,
    size: 'small',
    type: 'tertiary',
  };

  onActionMenuClick(population: PopulationItem) {
    this.selectedPopulation = population;
  }

  searchByName(searchTerm: string) {
    this.populations = [];
    this.isLoading = true;
    this.searchPopulations.emit(searchTerm);
    this.searchSuggestions$.next([]);
  }

  loadData(event: TableLazyLoadEvent) {
    if (event.last && event.last > this.populations.length - 20) {
      this.loadMorePopulations.emit();
    }
  }

  onCreateNewPopulationClick() {
    this.newPopulationCreated.emit();
  }
}
